import React from 'react';

import { Button } from '../ui/button';

import { Badge } from '../ui/badge';

import { Loader2, FileText, Download, ShieldCheck, Award, Eye, FolderInput, Package, Mail, QrCode, PenLine } from 'lucide-react';

import { BACKEND_URL, STATUS_COLORS, formatDate } from './dosHelpers';
import { PackTrackingBadge, NominativeTrackingBadge } from './DOSParts';

export function AttestationCard({ item, idx, onView, onDownload, onSendEmail, onSendNominativeSign, onClasserGED, gedLoading }) {
  const prov = item.attestation_provisoire;
  const def_ = item.attestation_definitive;

  return (
    <div className="border rounded-lg p-4 bg-white hover:shadow-sm transition-shadow" data-testid={`att-card-${idx}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-violet-700 font-medium">{item.reference}</span>
            <Badge className={`text-xs ${STATUS_COLORS[item.status_group] || 'bg-gray-500 text-white'}`}>
              {item.status_label || item.status}
            </Badge>
            <PackTrackingBadge tracking={item.pack_tracking} idx={`att-${idx}`} />
            <NominativeTrackingBadge tracking={item.nominative_tracking} idx={`att-${idx}`} />
          </div>
          <p className="text-sm text-gray-600 mt-1">{item.nom_complet}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs text-gray-400">Cree le {formatDate(item.created_at)}</div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onView(item.reference, 'cpcg')}
            className="text-xs h-7 px-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid={`att-view-cpcg-${idx}`}
            title="Dossier Maître : Conditions Particulières + Conditions Générales + Note"
          >
            <FileText className="h-3 w-3 mr-1" /> CP/CG
          </Button>
          <Button
            size="sm"
            onClick={() => onDownload(item.reference, 'cpcg')}
            className="text-xs h-7 px-2 bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid={`att-dl-cpcg-${idx}`}
            title="Télécharger le Dossier Maître (CP + CG + Note)"
          >
            <Download className="h-3 w-3 mr-1" /> CP/CG
          </Button>
          <Button
            size="sm"
            onClick={() => onView(item.reference, 'pack')}
            className="text-xs h-7 px-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
            data-testid={`att-view-pack-${idx}`}
            title="Pack complet : Attestation + CP + CG + Note (1 seul PDF à envoyer au Maître d'ouvrage)"
          >
            <Package className="h-3 w-3 mr-1" /> Pack complet
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDownload(item.reference, 'pack')}
            className="text-xs h-7 px-2 border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid={`att-dl-pack-${idx}`}
            title="Télécharger le Pack complet (1 seul PDF)"
          >
            <Download className="h-3 w-3 mr-1" /> Pack
          </Button>
          {(item.signature_statut === 'signed' || item.is_acquitted || prov || def_) && (
            <>
              <Button
                size="sm"
                onClick={() => onView(item.reference, 'nominative')}
                className="text-xs h-7 px-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                data-testid={`att-view-nominative-${idx}`}
                title="Attestation nominative officielle — générée à partir de l'attestation et des CP/CG signés, avec QR code de vérification"
              >
                <Award className="h-3 w-3 mr-1" /> Attestation nominative
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDownload(item.reference, 'nominative')}
                className="text-xs h-7 px-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                data-testid={`att-dl-nominative-${idx}`}
                title="Télécharger l'Attestation nominative officielle (QR de vérification)"
              >
                <Download className="h-3 w-3 mr-1" /> Nominative
              </Button>
              {onSendNominativeSign && (
                item.nominative_signature_statut === 'signed' ? (
                  <span
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-teal-100 text-teal-800 text-xs rounded font-medium"
                    title="Attestation nominative signée électroniquement par le Maître d'ouvrage (OTP email vérifié)"
                    data-testid={`att-nom-signed-${idx}`}
                  >
                    <ShieldCheck className="h-3 w-3" /> Signée MO
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSendNominativeSign(item.reference, item?.identite?.email || '')}
                    className="text-xs h-7 px-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                    data-testid={`att-nom-sign-${idx}`}
                    title={item.nominative_invitation_sent
                      ? 'Lien de signature déjà envoyé — renvoyer au Maître d\'ouvrage'
                      : "Envoyer l'attestation nominative au Maître d'ouvrage pour signature électronique (code OTP par email, lien valable 7 jours)"}
                  >
                    <PenLine className="h-3 w-3 mr-1" /> {item.nominative_invitation_sent ? 'Relancer signature' : 'Signature MO'}
                  </Button>
                )
              )}
            </>
          )}
          {onSendEmail && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSendEmail(
                item.reference,
                item?.identite?.email || item?.adherent_email || '',
                item?.identite?.nom_dirigeant || item?.identite?.raison_sociale || ''
              )}
              className="text-xs h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid={`att-send-mo-${idx}`}
              title="Envoyer le Pack complet par email au Maître d'ouvrage (lien sécurisé 7 jours)"
            >
              <Mail className="h-3 w-3 mr-1" /> Envoyer au MO
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => window.open(`${BACKEND_URL}/verify/domium/${item.reference}`, '_blank', 'noopener,noreferrer')}
            className="text-xs h-7 px-2 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
            data-testid={`att-test-qr-${idx}`}
            title="Ouvrir la page publique de vérification du QR code dans un nouvel onglet"
          >
            <QrCode className="h-3 w-3 mr-1" /> Tester QR
          </Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Attestation Draft / Provisoire */}
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <ShieldCheck className="h-4 w-4 text-amber-600" />
            <span className="font-medium text-amber-800 text-sm">
              {prov ? 'Attestation Provisoire' : 'Attestation DRAFT'}
            </span>
          </div>
          {prov ? (
            <div className="space-y-1 text-xs text-gray-600">
              <p>N: {prov.numero}</p>
              <p>Emise le: {formatDate(prov.date_emission)}</p>
              {prov.signer_name && <p>Signe par: {prov.signer_name}</p>}
              <p>Verification: <span className="font-mono">{prov.verification_code}</span></p>
              <p className="text-amber-700 italic">{prov.validite}</p>
            </div>
          ) : (
            <p className="text-xs text-amber-600 italic">Brouillon disponible</p>
          )}
          <div className="flex gap-2 mt-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => onView(item.reference, 'draft')} className="text-xs h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-100" data-testid={`att-view-draft-${idx}`}>
              <Eye className="h-3 w-3 mr-1" /> Visualiser
            </Button>
            <Button size="sm" onClick={() => onDownload(item.reference, 'draft')} className="text-xs h-7 px-2 bg-amber-500 hover:bg-amber-600 text-white" data-testid={`att-dl-draft-${idx}`}>
              <Download className="h-3 w-3 mr-1" /> Telecharger
            </Button>
            {onClasserGED && (
              <Button size="sm" variant="outline" onClick={() => onClasserGED(item.reference, 'draft')} disabled={gedLoading === `${item.reference}-draft`} className="text-xs h-7 px-2 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`att-ged-draft-${idx}`}>
                {gedLoading === `${item.reference}-draft` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FolderInput className="h-3 w-3 mr-1" />}
                Classer GED
              </Button>
            )}
          </div>
        </div>

        {/* Attestation Signee / Definitive */}
        {(def_ || item.status === 'ACTIVE' || item.status === 'PAID_CONFIRMED' || item.status === 'ATTESTATION_PROVISOIRE') && (
          <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Award className="h-4 w-4 text-emerald-600" />
              <span className="font-medium text-emerald-800 text-sm">
                {def_ ? 'Attestation Definitive' : 'Attestation Signee'}
              </span>
            </div>
            {def_ ? (
              <div className="space-y-1 text-xs text-gray-600">
                <p>N: {def_.numero}</p>
                <p>Emise le: {formatDate(def_.date_emission)}</p>
                <p>Date effet: {formatDate(def_.date_effet)}</p>
                <p>Date fin: {formatDate(def_.date_fin)}</p>
                <p>Duree: {def_.duree_ans} ans</p>
                <p>Verification: <span className="font-mono">{def_.verification_code}</span></p>
                <Badge className="bg-emerald-600 text-white text-xs mt-1 font-semibold">{def_.statut}</Badge>
              </div>
            ) : (
              <p className="text-xs text-emerald-600 italic">Document signe disponible</p>
            )}
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => onView(item.reference, 'signed')} className="text-xs h-7 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100" data-testid={`att-view-signed-${idx}`}>
                <Eye className="h-3 w-3 mr-1" /> Visualiser
              </Button>
              <Button size="sm" onClick={() => onDownload(item.reference, 'signed')} className="text-xs h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`att-dl-signed-${idx}`}>
                <Download className="h-3 w-3 mr-1" /> Telecharger
              </Button>
              {onClasserGED && (
                <Button size="sm" variant="outline" onClick={() => onClasserGED(item.reference, 'signed')} disabled={gedLoading === `${item.reference}-signed`} className="text-xs h-7 px-2 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`att-ged-signed-${idx}`}>
                  {gedLoading === `${item.reference}-signed` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FolderInput className="h-3 w-3 mr-1" />}
                  Classer GED
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
